/**
 * This is a legacy file
 * Utils should be moved to the typescript file ./applicationSelectors.ts
 * Or other more specific files as necessary
 *
 * As you move utils to the typescript file, please remove them from this file
 * and add an export from the typescript file so that we don't break any imports
 * Once this file is all import shims, we can delete it
 * */

// @flow
import _ from 'lodash';

import { isoToSlashDate, slashDateToISO } from 'routes/marketplace-ede/web-ui/utils/format';
import { UPDATE_APPLICANT } from 'routes/marketplace-ede/queries/productApplication/updateApplicant';
import { isValidCMSEmail } from 'routes/marketplace-ede/web-ui/utils/validate';

import { getPrimaryMember, isMemberRequestingCoverage } from './selectors';
import { formatContactPhones, formatApplicantAddress } from './applicationSelectors';
import { getEdeMemberDemographic } from './edeMember';
import { PrimaryContactInfoType } from '../types/profile/profile.flow';

import type { EdeApplicationPayloadType, PhoneNumberFieldsType } from '../types/ede/edeApplication.flow';
import type { AddressFieldsType } from '../types/ede/edeMember.flow';
import type {
  ProfileType,
  // PersonPhoneType,
} from '../types/profile/profile.flow';

/** Typescript re-exports */
export { mapIdProofingToDocumentPayload } from './applicationSelectors';

export const getPreviousIncomeEstimate = (edeApplication: EdeApplicationPayloadType): number => {
  const primaryMember = getPrimaryMember(edeApplication);
  const memberIdentifier = _.get(primaryMember, 'memberIdentifier');
  const taxHouseholds = _.get(edeApplication, 'computed.taxHouseholds', []);
  const primaryTaxHousehold = _.find(taxHouseholds, (household) => {
    const taxHouseholdMemberIdentifiers = _.get(
      household,
      'taxHouseHoldComposition.taxHouseholdMemberIdentifiers',
      [],
    );
    return _.includes(taxHouseholdMemberIdentifiers, memberIdentifier);
  });

  return _.get(primaryTaxHousehold, 'annualIncome.attestedAnnualTaxHouseholdIncomeAmount') || 0;
};

export const getPrimaryContactInfo = (
  edeApplication: EdeApplicationPayloadType | {},
): PrimaryContactInfoType => {
  const { coverageState, spokenLanguageType, writtenLanguageType } = _.get(
    edeApplication,
    'attestations.application',
    {},
  );
  const { email, primaryPhoneNumber, secondaryPhoneNumber, mobileNotificationPhoneNumber } =
    _.get(edeApplication, 'attestations.application.contactInformation') || {};
  const primaryMember = getPrimaryMember(edeApplication);
  const demographic = getEdeMemberDemographic(primaryMember);
  const name = _.get(demographic, 'name', {});

  return {
    ...demographic,
    ...name,
    memberIdentifier: primaryMember.memberIdentifier,
    coverageState,
    email,
    primaryPhoneNumber,
    secondaryPhoneNumber,
    mobileNotificationPhoneNumber,
    spokenLanguageType,
    writtenLanguageType,
    isPrimaryApplying: isMemberRequestingCoverage(primaryMember),
  };
};

type ApplicantInput = {
  householdContactIndicator: boolean,
  firstName: string,
  lastName: string,
  middleName?: string,
  suffix?: string,
  birthDate: string,
  noHomeAddressIndicator?: boolean,
  liveOutsideStateTemporarilyIndicator?: boolean,
  mailingAddress?: AddressFieldsType,
  homeAddress?: AddressFieldsType,
  transientAddress?: AddressFieldsType,
};

type CreateEdeAppPayloadType = {
  application: {
    coverageYear: string,
    coverageState: string,
    contactInformation: {
      email: string,
      primaryPhoneNumber?: PhoneNumberFieldsType,
      secondaryPhoneNumber?: PhoneNumberFieldsType,
      mobileNotificationPhoneNumber?: PhoneNumberFieldsType,
    },
  },
  applicationMembers: Array<ApplicantInput>,
};

export const getStateCode = address => _.get(address, 'stateCode');

const ISO_REGEX = /(\d{4})(-\d{2}){2}/;

export const mapDataToCreateEdeAppPayload = (
  profile: ProfileType,
  primaryAttestations: Object,
  noHomeAddressIndicator: boolean,
  productEffectiveYear: string,
): CreateEdeAppPayloadType => {
  const { email, appEmail, phones, addresses, firstName, lastName, suffix, dob } = profile;
  const { primaryPhoneNumber, secondaryPhoneNumber } = formatContactPhones(phones);
  const { homeAddress, mailingAddress, transientAddress } = formatApplicantAddress(addresses);
  // get coverageState & coverageState from primaryAttestations
  // fallback to profile address and product effective date or current year (or next year if past nov 1st)
  const now = new Date();
  const currentCoverageYear = now.getMonth() > 9 ? now.getFullYear() + 1 : now.getFullYear();
  const coverageYear =
    _.get(primaryAttestations, 'coverageYear') || productEffectiveYear || currentCoverageYear.toString();
  const coverageState =
    _.get(primaryAttestations, 'coverageState') || getStateCode(homeAddress || mailingAddress);

  const contactInformation: Object = {};

  if (appEmail || isValidCMSEmail(email)) {
    contactInformation.email = appEmail || email;
  }

  if (primaryPhoneNumber) contactInformation.primaryPhoneNumber = primaryPhoneNumber;
  if (secondaryPhoneNumber) contactInformation.secondaryPhoneNumber = secondaryPhoneNumber;

  const applicationPayload = {
    coverageYear: Number(coverageYear),
    coverageState,
    contactInformation,
  };
  const noHomeAddress = _.isBoolean(noHomeAddressIndicator)
    ? noHomeAddressIndicator
    : !homeAddress.streetName1;

  const applicant = {
    firstName,
    lastName,
    suffix,
    birthDate: slashDateToISO(isoToSlashDate(dob.match(ISO_REGEX)[0])),
    householdContactIndicator: true,
    noHomeAddressIndicator: noHomeAddress,
  };

  const hasOutOfStateAddress = noHomeAddress
    ? coverageState !== mailingAddress.stateCode
    : coverageState !== homeAddress.stateCode;

  // createEdeApp required to have mailingAddress in the payload;
  applicant.mailingAddress = mailingAddress || homeAddress;
  if (homeAddress?.streetName1) applicant.homeAddress = homeAddress;
  if (transientAddress && hasOutOfStateAddress) applicant.transientAddress = transientAddress;

  return {
    application: applicationPayload,
    applicationMembers: [applicant],
  };
};

export const mapPrimaryAttestationToEdePayload = ({
  profile,
  primaryAttestations,
  primaryInsuredMemberIdentifier,
}: {
  profile: ProfileType,
  primaryAttestations: Object,
  primaryInsuredMemberIdentifier: string,
}) => {
  const {
    hispanicOriginIndicator,
    ethnicity,
    race,
    isApplying,
    coverageState,
    preferredSpokenLanguage,
    preferredWrittenLanguage,
    preferredContactMethod,
    eTextPhoneNumber,
    sexAssignedAtBirth,
    sexAssignedAtBirthOtherText,
    gender,
    genderDifferentTermText,
    sexualOrientation,
    sexualOrientationDifferentTermText,
  } = primaryAttestations;
  const { sex, addresses, phones } = profile;
  const { primaryPhoneNumber } = formatContactPhones(phones);
  const demographicPayload = {
    sex,
    sexAssignedAtBirth: sexAssignedAtBirth || null,
    sexAssignedAtBirthOtherText: sexAssignedAtBirthOtherText || null,
    gender: gender || null,
    genderDifferentTermText: genderDifferentTermText || null,
    sexualOrientation: sexualOrientation || null,
    sexualOrientationDifferentTermText: sexualOrientationDifferentTermText || null,
  };
  if (_.isBoolean(hispanicOriginIndicator)) {
    demographicPayload.hispanicOriginIndicator = hispanicOriginIndicator;
  }

  // TODO: update race, otherRaceText & ethnicity attestations
  if (!_.isEmpty(ethnicity)) {
    const { ethnicities, other } = ethnicity;
    if (hispanicOriginIndicator) {
      if (!_.isEmpty(ethnicities)) {
        demographicPayload.ethnicity = _.map(ethnicities, e => e.id);
      }
      if (other && _.includes(demographicPayload.ethnicity, 'OTHER')) {
        demographicPayload.otherEthnicityText = other;
      }
    }
  }
  if (!_.isEmpty(race)) {
    if (!_.isEmpty(race.races)) {
      demographicPayload.race = _.map(race.races, r => r.id);
    }
    if (race.other && _.includes(demographicPayload.race, 'OTHER')) {
      demographicPayload.otherRaceText = race.other;
    }
  }

  const transientAddress = _.find(addresses, { addressType: 'TRANSIENT' }, {});
  const homeAddress = _.find(addresses, { addressType: 'HOME' });
  const mailingAddress = _.find(addresses, { addressType: 'MAILING' });
  const hasOutOfStateAddress = homeAddress
    ? coverageState !== _.get(homeAddress, 'state')
    : coverageState !== _.get(mailingAddress, 'state');

  demographicPayload.liveOutsideStateTemporarilyIndicator =
    hasOutOfStateAddress && _.get(transientAddress, 'state') === coverageState;

  return {
    application: {
      contactInformation: {
        mobileNotificationPhoneNumber: eTextPhoneNumber || null,
        primaryPhoneNumber: primaryPhoneNumber || null,
      },
      spokenLanguageType: preferredSpokenLanguage || null,
      writtenLanguageType: preferredWrittenLanguage || null,
      contactMethod: [preferredContactMethod] || null,
    },
    members: [
      {
        memberIdentifier: primaryInsuredMemberIdentifier,
        edePayload: {
          demographic: demographicPayload,
          requestingCoverageIndicator: isApplying,
        },
      },
    ],
  };
};

export const reconcileMembers = (members: Array<EdeMemberType>, productApplicants: Array<Object>) =>
  _.reduce(
    productApplicants,
    (list, applicant) => {
      const match = _.find(list, (m) => {
        if (!m.memberIdentifier) return false;
        const { birthDate, sex } = getEdeMemberDemographic(m) || {};
        return applicant.dob === birthDate && applicant.sex === sex;
      });
      return match ? list : _.concat(list, [applicant]);
    },
    [...members],
  );

export const isSamePerson = (applicant: Object, member: EdeMember) => {
  const {
    sex,
    birthDate,
    name: { firstName, lastName },
  } = _.get(member, 'attestations.demographic', {});
  return (
    applicant.firstName === firstName &&
    applicant.lastName === lastName &&
    applicant.sex === sex &&
    applicant.dob === birthDate
  );
};

export const isExistingMember = (applicant: Object, members: Array<EdeMember>) =>
  _.find(members, member => isSamePerson(applicant, member));

export const filterEdeMembers = (applicants: Array<Object>, members: Array<EdeMember>) => {
  const productApplicants = [];
  const edeReadyMembers = [];

  _.forEach(applicants, (applicant) => {
    if (
      applicant.firstName &&
      applicant.lastName &&
      applicant.sex &&
      applicant.dob &&
      !isExistingMember(applicant, members)
    ) {
      edeReadyMembers.push(applicant);
    } else {
      productApplicants.push(applicant);
    }
  });
  return [edeReadyMembers, productApplicants];
};

export const createMembers = (
  appID: string,
  productAppId: string,
  productMembers: Array<Object>,
  addEdeMember: Function,
) => {
  const payload = _.map(productMembers, m => ({
    requestingCoverageIndicator: true,
    demographic: {
      name: {
        firstName: m.firstName,
        lastName: m.lastName,
      },
      sex: m.sex,
      birthDate: m.dob,
    },
  }));

  return addEdeMember({
    variables: {
      appID,
      productAppId,
      payload,
    },
  });
};

export const syncApplicantsAndMembers = async ({ apolloClient, applicants, persons, members }) => {
  const unsyncedPersons = applicants.filter(applicant => !applicant.externalId);

  Promise.all([
    unsyncedPersons.map((applicant) => {
      const matchingPerson = persons.find(person => person.id === applicant.personId);
      const matchingMember = members.find(member => isSamePerson(matchingPerson, member));
      if (!matchingMember) return Promise.resolve();

      const applicantUpdate = apolloClient.mutate({
        mutation: UPDATE_APPLICANT,
        variables: {
          applicantId: applicant.id,
          payload: { externalId: matchingMember.memberIdentifier },
        },
      });
      return applicantUpdate;
    }),
  ]);
};
